import { numFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Badge,
  Box,
  Button,
  Chip,
  Typography,
  Paper,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { withTranslation } from 'react-i18next';
import { useUserStore } from 'store';
import { can } from '../../utils/access';
import SupplierOrderStatusBadge from './SupplierOrderStatusBadge';
import SupplierOrderDetailDialog from './SupplierOrderDetailDialog';
import PendingSupplierPartsTab, { pendingSourceCount } from './PendingSupplierPartsTab';
import InboundDeliveryNotesTab from './inbound/InboundDeliveryNotesTab';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import DataTable from '../shared/DataTable';
import {
  useListPreferences,
  FilterButton,
  FilterPanel,
  FilterSection,
  SearchInput,
  DateRangeFilter,
  getDateRange,
  sanitizeDateString,
  FilterSummary,
} from '../shared/ListFilters';
import { formatPrice } from '../../utils/vat';
import { formatDate } from '../../utils';

const STORAGE_KEY = 'supplier_orders_list_preferences';

const defaultPreferences = {
  sortField: 'ordered_at',
  sortDirection: 'desc',
  searchQuery: '',
  filters: {
    status: '',
    supplier_type: '',
    dateRangeType: 'all',
    dateFrom: '',
    dateTo: '',
  },
};

const BASE_TABS = [
  { id: 'ordered', labelKey: 'purchaseOrders.tabs.ordered' },
  { id: 'pending', labelKey: 'purchaseOrders.tabs.unordered' },
];

const MainSupplierOrdersView = ({ t }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const inboundAvailable = currentOrganization?.inbound_delivery_notes_available ?? false;
  const canManageOrders = can(currentOrganization, 'supplier_orders.manage');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Tab index / URL sync is array-index based — derive from the gated array.
  const TABS = useMemo(
    () =>
      inboundAvailable
        ? [...BASE_TABS, { id: 'inbound', labelKey: 'inboundDeliveries.tabLabel' }]
        : BASE_TABS,
    [inboundAvailable]
  );

  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const index = (inboundAvailable
        ? [...BASE_TABS, { id: 'inbound' }]
        : BASE_TABS
      ).findIndex((tab) => tab.id === tabParam);
      if (index !== -1) return index;
    }
    return 0;
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [inboundNewCount, setInboundNewCount] = useState(0);
  // Orders with a lähete parked on them (explicit match, still to receive): the
  // Tilatut tab badge, so a delivery that arrived is noticed where it is handled.
  const [ordersWithLahete, setOrdersWithLahete] = useState(0);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  // Deep link from a lähete line ("Tilattu · Örum T-…"): /supplier-orders?order=<id>
  const orderParam = searchParams.get('order');
  useEffect(() => {
    if (!orderParam) return;
    setSelectedOrderId(Number(orderParam));
    setDetailDialogOpen(true);
  }, [orderParam]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Sync tab from URL changes (browser back/forward)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const index = TABS.findIndex((tab) => tab.id === tabParam);
      if (index !== -1 && index !== activeTab) {
        setActiveTab(index);
      }
    }
  }, [searchParams, TABS]);

  // Clamp if the gated tab list shrinks (org switch)
  useEffect(() => {
    if (activeTab >= TABS.length) setActiveTab(0);
  }, [TABS.length, activeTab]);

  const handleTabChange = useCallback((_, newValue) => {
    setActiveTab(newValue);
    setSearchParams({ tab: TABS[newValue].id }, { replace: true });
  }, [setSearchParams, TABS]);

  // Inbound delivery note badge (Saapuneet lähetteet)
  const fetchInboundCounts = useCallback(async () => {
    if (!inboundAvailable) return;
    try {
      const resp = await axios.get('/api/inbound-delivery-notes/counts/');
      setInboundNewCount(resp.data.new || 0);
      setOrdersWithLahete(resp.data.orders_with_lahete || 0);
    } catch {
      // Badge just won't show
    }
  }, [inboundAvailable]);

  useEffect(() => {
    fetchInboundCounts();
  }, [fetchInboundCounts]);

  // Fetch pending work order count on mount (so badge shows even on Tilatut tab)
  const fetchPendingCount = useCallback(async () => {
    try {
      const resp = await axios.get('/api/supplier-orders/pending-parts/');
      // Same count as the tab itself: distinct work orders + lähetteet.
      setPendingCount(pendingSourceCount(resp.data.integrations || []));
    } catch {
      // Silently ignore — badge just won't show
    }
  }, []);

  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount]);

  // Use localStorage-persisted preferences
  const {
    preferences,
    updatePreferences,
    updateFilters,
    resetFilters: resetLocalFilters,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // Extract filter values
  const statusFilter = preferences.filters.status || '';
  const supplierType = preferences.filters.supplier_type || '';
  const dateRangeType = preferences.filters.dateRangeType || 'all';
  const dateFrom = sanitizeDateString(preferences.filters.dateFrom);
  const dateTo = sanitizeDateString(preferences.filters.dateTo);

  // Convert date range to backend filter params
  const dateFilterParams = useMemo(() => {
    if (dateRangeType === 'all') return {};
    const { from, to } = getDateRange(dateRangeType, dateFrom, dateTo);
    const params = {};
    if (from) params.date_from = from.toISOString().split('T')[0];
    if (to) params.date_to = to.toISOString().split('T')[0];
    return params;
  }, [dateRangeType, dateFrom, dateTo]);

  // Build server-side filters
  const serverFilters = useMemo(() => {
    const filters = {};
    if (statusFilter) filters.status = statusFilter;
    if (supplierType) filters.supplier_type = supplierType;
    if (dateFilterParams.date_from) filters.date_from = dateFilterParams.date_from;
    if (dateFilterParams.date_to) filters.date_to = dateFilterParams.date_to;
    return filters;
  }, [statusFilter, supplierType, dateFilterParams]);

  // Use server-side pagination hook
  const {
    data: orders,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    sortField,
    sortDirection,
    handleSort,
    refetch,
    setFilters,
  } = usePaginatedList('/api/supplier-orders/', {
    initialFilters: serverFilters,
    defaultSortField: 'ordered_at',
    defaultSortDirection: 'desc',
    defaultPageSize: 50,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Update server filters when local filters change
  React.useEffect(() => {
    setFilters(serverFilters);
  }, [serverFilters, setFilters]);

  // Coming back to Tilatut after receiving a lähete on the inbound tab: the list
  // hook lives here and would otherwise show the stale row.
  const tabSeenRef = React.useRef(activeTab);
  React.useEffect(() => {
    if (activeTab === 0 && tabSeenRef.current !== 0) {
      refetch();
      fetchInboundCounts();
    }
    tabSeenRef.current = activeTab;
  }, [activeTab, refetch, fetchInboundCounts]);

  // Open a lähete parked on an order: the inbound tab reads ?note= when it mounts.
  const openInboundNote = useCallback(
    (noteId) => {
      const index = TABS.findIndex((tab) => tab.id === 'inbound');
      if (index === -1) return;
      setDetailDialogOpen(false);
      setSearchParams({ tab: 'inbound', note: String(noteId) }, { replace: true });
      setActiveTab(index);
    },
    [TABS, setSearchParams]
  );

  const renderInboundChip = (order) => {
    const inbound = order.inbound_delivery;
    if (!inbound) return null;
    const partial = inbound.covered_lines < inbound.total_lines;
    return (
      <Chip
        icon={<MoveToInboxIcon sx={{ fontSize: 14 }} />}
        label={
          partial
            ? t('purchaseOrders.inboundChipPartial', { covered: inbound.covered_lines, total: inbound.total_lines })
            : t('purchaseOrders.inboundChip')
        }
        size="small"
        clickable
        onClick={(e) => {
          e.stopPropagation();
          openInboundNote(inbound.note_id);
        }}
        data-testid={`order-inbound-chip-${order.id}`}
        sx={{ bgcolor: '#fef3e2', color: '#9c4221', fontWeight: 600, fontSize: '12px', '& .MuiChip-icon': { color: '#9c4221' } }}
      />
    );
  };

  // Sync search query from localStorage preferences to hook
  React.useEffect(() => {
    setSearchQuery(preferences.searchQuery || '');
  }, [preferences.searchQuery, setSearchQuery]);

  const resetFilters = useCallback(() => {
    resetLocalFilters();
  }, [resetLocalFilters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter) count++;
    if (supplierType) count++;
    if (dateRangeType !== 'all') count++;
    return count;
  }, [statusFilter, supplierType, dateRangeType]);

  // Build filter summary chips
  const filterSummaryChips = useMemo(() => {
    const chips = [];
    if (statusFilter) {
      chips.push({ key: 'status', label: t('purchaseOrders.status.' + statusFilter) });
    }
    if (supplierType) {
      chips.push({ key: 'supplier', label: supplierType });
    }
    if (dateRangeType === 'today') {
      chips.push({ key: 'date', label: t('Today') });
    } else if (dateRangeType === 'this_week') {
      chips.push({ key: 'date', label: t('This week') });
    } else if (dateRangeType === 'this_month') {
      chips.push({ key: 'date', label: t('This month') });
    } else if (dateRangeType === 'custom') {
      if (dateFrom && dateTo) {
        chips.push({ key: 'date', label: `${dateFrom} – ${dateTo}` });
      } else if (dateFrom) {
        chips.push({ key: 'date', label: `${t('From')} ${dateFrom}` });
      } else if (dateTo) {
        chips.push({ key: 'date', label: `${t('To')} ${dateTo}` });
      }
    }
    return chips;
  }, [statusFilter, supplierType, dateRangeType, dateFrom, dateTo, t]);

  // Error snackbar state
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const prevError = React.useRef(error);

  React.useEffect(() => {
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  const handleRowClick = (order) => {
    setSelectedOrderId(order.id);
    setDetailDialogOpen(true);
  };

  const tabLabel = (label, count) =>
    isMobile ? (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        {label}
        {count > 0 && (
          <Box
            component="span"
            sx={{
              minWidth: 18,
              height: 18,
              px: '5px',
              borderRadius: '9px',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: '11px',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {count > 999 ? '999+' : count}
          </Box>
        )}
      </Box>
    ) : (
      <Badge badgeContent={count} color="primary" max={999} sx={{ '& .MuiBadge-badge': { right: -16, top: 2 } }}>
        {label}
      </Badge>
    );

  return (
    <Box data-testid="supplier-orders-page">
      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowErrorSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {t('Error')}: {error}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: { xs: '1.6rem', sm: '2.125rem' },
            }}
          >
            {t('purchaseOrders.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('purchaseOrders.subtitle')}
          </Typography>
        </Box>
        {canManageOrders && (
          <Button
            variant="contained"
            startIcon={<AddShoppingCartIcon />}
            onClick={() => navigate('/supplier-orders/new')}
            sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600, flexShrink: 0 }}
            data-testid="new-supplier-order"
          >
            {t('orderBuilder.newOrderButton')}
          </Button>
        )}
      </Box>

      {/* Tabs — on phones the three labels do not fit side by side: the row scrolls
          and the count rides inside the label, since a floating badge needs
          overflow room that a scrolling tab row cannot give it. */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons={false}
        sx={{
          mb: 2.5,
          ...(isMobile
            ? { '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1, fontSize: '13.5px' } }
            : {
                overflow: 'visible',
                '& .MuiTabs-scroller': { overflow: 'visible !important' },
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, overflow: 'visible' },
              }),
        }}
      >
        <Tab label={tabLabel(t('purchaseOrders.tabs.ordered'), ordersWithLahete)} data-testid="ordered-tab" />
        <Tab label={tabLabel(t('purchaseOrders.tabs.unordered'), pendingCount)} />
        {inboundAvailable && (
          <Tab
            data-testid="inbound-tab"
            label={tabLabel(t('inboundDeliveries.tabLabel'), inboundNewCount)}
          />
        )}
      </Tabs>

      {/* Tab 1: Pending Parts */}
      {activeTab === 1 && (
        <PendingSupplierPartsTab
          onPendingCountChange={setPendingCount}
          onOrderCreated={() => {
            handleTabChange(null, 0);
            refetch();
          }}
        />
      )}

      {/* Tab 2: Inbound delivery notes (Saapuneet lähetteet) */}
      {inboundAvailable && activeTab === 2 && (
        <InboundDeliveryNotesTab
          onCountsChange={(counts) => {
            setInboundNewCount(counts.new || 0);
            setOrdersWithLahete(counts.orders_with_lahete || 0);
          }}
        />
      )}

      {/* Tab 0: Ordered list */}
      {activeTab === 0 && <>
      {/* Search and Filter Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery || ''}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={t('purchaseOrders.searchPlaceholder')}
        />
        <FilterButton
          t={t}
          activeCount={activeFilterCount}
          isOpen={filtersOpen}
          onClick={toggleFiltersOpen}
        />
      </Box>

      {/* Active Filter Summary */}
      <FilterSummary chips={filterSummaryChips} onClear={resetFilters} t={t} />

      {/* Filter Panel */}
      <FilterPanel
        open={filtersOpen}
        resultCount={totalCount}
        onReset={resetFilters}
        resetDisabled={activeFilterCount === 0}
        t={t}
      >
        <FilterSection title={t('Status')}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('Status')}</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => updateFilters({ status: e.target.value })}
              label={t('Status')}
            >
              <MenuItem value="">{t('purchaseOrders.allStatuses')}</MenuItem>
              <MenuItem value="pending">{t('purchaseOrders.status.pending')}</MenuItem>
              <MenuItem value="confirmed">{t('purchaseOrders.status.confirmed')}</MenuItem>
              <MenuItem value="delivered">{t('purchaseOrders.status.delivered')}</MenuItem>
              <MenuItem value="partially_delivered">{t('purchaseOrders.status.partially_delivered')}</MenuItem>
              <MenuItem value="cancelled">{t('purchaseOrders.status.cancelled')}</MenuItem>
            </Select>
          </FormControl>
        </FilterSection>

        <FilterSection title={t('purchaseOrders.supplier')} dividerTop>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('purchaseOrders.supplier')}</InputLabel>
            <Select
              value={supplierType}
              onChange={(e) => updateFilters({ supplier_type: e.target.value })}
              label={t('purchaseOrders.supplier')}
            >
              <MenuItem value="">{t('purchaseOrders.allSuppliers')}</MenuItem>
              <MenuItem value="orum">Örum</MenuItem>
              <MenuItem value="atoy">Atoy</MenuItem>
              <MenuItem value="meko">MEKO</MenuItem>
              <MenuItem value="ratparts">Rengastien Autotarvike</MenuItem>
              <MenuItem value="email">{t('purchaseOrders.supplierEmail')}</MenuItem>
            </Select>
          </FormControl>
        </FilterSection>

        <FilterSection title={t('purchaseOrders.orderedAt')} dividerTop>
          <DateRangeFilter
            dateRangeType={dateRangeType}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onTypeChange={(type) => {
              if (type === 'all' || type !== 'custom') {
                updateFilters({ dateRangeType: type, dateFrom: '', dateTo: '' });
              } else {
                updateFilters({ dateRangeType: type });
              }
            }}
            onFromChange={(date) => {
              updateFilters({ dateRangeType: 'custom', dateFrom: date });
            }}
            onToChange={(date) => {
              updateFilters({ dateRangeType: 'custom', dateTo: date });
            }}
            t={t}
          />
        </FilterSection>
      </FilterPanel>

      {/* Desktop Table */}
      <DataTable
        columns={[
          {
            key: 'order_reference',
            label: t('purchaseOrders.order'),
            noWrap: true,
            render: (order) => (
              <Typography sx={{ fontWeight: 500, ...numFont, fontSize: '13px' }}>
                {order.display_reference || '—'}
              </Typography>
            ),
          },
          {
            key: 'supplier',
            label: t('purchaseOrders.supplier'),
            maxWidth: 200,
            render: (order) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {order.supplier_display_name}
              </Typography>
            ),
          },
          {
            key: 'work_order',
            label: t('purchaseOrders.workOrder'),
            maxWidth: 200,
            render: (order) => (
              <Box>
                {(order.work_orders || []).map((wo) => (
                  <Typography key={wo.maintenance_id} sx={{ fontSize: '13px', color: 'text.primary' }}>
                    {wo.vehicle_registration} #{wo.work_number}
                  </Typography>
                ))}
                {(order.parts_sales || []).map((sale) => (
                  <Typography key={`s-${sale.parts_sale_id}`} sx={{ fontSize: '13px', color: 'text.primary' }}>
                    {t('purchaseOrders.pendingParts.deliveryNote', { number: sale.number })}
                  </Typography>
                ))}
              </Box>
            ),
          },
          {
            key: 'vehicle',
            label: t('purchaseOrders.vehicle'),
            render: (order) => (
              <Box>
                {(order.work_orders || []).map((wo) => (
                  <Typography key={wo.maintenance_id} sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {wo.vehicle_manufacturer} {wo.vehicle_model}
                  </Typography>
                ))}
                {(order.parts_sales || []).map((sale) => (
                  <Typography key={`s-${sale.parts_sale_id}`} sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {sale.customer_name}
                  </Typography>
                ))}
              </Box>
            ),
          },
          {
            key: 'line_count',
            label: t('purchaseOrders.partsCount'),
            align: 'center',
            noWrap: true,
            render: (order) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {order.line_count}
              </Typography>
            ),
          },
          {
            key: 'total_price',
            label: t('purchaseOrders.total'),
            align: 'right',
            noWrap: true,
            render: (order) => (
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                {formatPrice(order.total_price)} €
              </Typography>
            ),
          },
          {
            key: 'ordered_at',
            label: t('purchaseOrders.orderedAt'),
            sortable: true,
            noWrap: true,
            render: (order) => (
              <Box>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {order.ordered_by_name}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                  {formatDate(order.ordered_at)}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'status',
            label: t('Status'),
            noWrap: true,
            render: (order) => <SupplierOrderStatusBadge status={order.status} />,
          },
          {
            key: 'inbound_delivery',
            label: t('purchaseOrders.inboundColumn'),
            width: '1%',
            noWrap: true,
            render: (order) => renderInboundChip(order),
          },
        ]}
        data={orders}
        loading={loading}
        onRowClick={handleRowClick}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        testIdPrefix="supplier-order-row"
        emptyIcon={<LocalShippingIcon />}
        emptyTitle={searchQuery || activeFilterCount > 0 ? t('purchaseOrders.noOrdersMatch') : t('purchaseOrders.noOrders')}
        emptyDescription={t('purchaseOrders.noOrdersDescription')}
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Mobile Cards */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {/* Loading overlay for refetch */}
        {loading && orders.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && orders.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : orders.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <LocalShippingIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {searchQuery || activeFilterCount > 0 ? t('purchaseOrders.noOrdersMatch') : t('purchaseOrders.noOrders')}
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
              {t('purchaseOrders.noOrdersDescription')}
            </Typography>
          </Paper>
        ) : (
          <>
            {orders.map((order) => (
              <Paper
                key={order.id}
                data-testid={`supplier-order-row-${order.id}`}
                elevation={0}
                onClick={() => handleRowClick(order)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        bgcolor: 'rgba(49, 130, 206, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'secondary.main',
                        flexShrink: 0,
                      }}
                    >
                      <LocalShippingIcon />
                    </Box>

                    {/* Order Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            ...numFont,
                            color: 'text.primary',
                          }}
                        >
                          {order.display_reference || '—'}
                        </Typography>
                        <SupplierOrderStatusBadge status={order.status} />
                        {renderInboundChip(order)}
                      </Box>

                      {(order.work_orders || []).map((wo) => (
                        <Typography key={wo.maintenance_id} sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                          {wo.vehicle_registration} #{wo.work_number}
                        </Typography>
                      ))}
                      {(order.parts_sales || []).map((sale) => (
                        <Typography key={`s-${sale.parts_sale_id}`} sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                          {t('purchaseOrders.pendingParts.deliveryNote', { number: sale.number })}
                          {sale.customer_name ? ` \u00b7 ${sale.customer_name}` : ''}
                        </Typography>
                      ))}

                      <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                        {order.supplier_display_name}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Amount and Date */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'text.primary' }}>
                      {formatPrice(order.total_price)} €
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      {formatDate(order.ordered_at)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}

            {/* Mobile Pagination Controls */}
            <PaginationControls
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              loading={loading}
            />
          </>
        )}
      </Box>

      </>}

      {/* Detail Dialog */}
      <SupplierOrderDetailDialog
        open={detailDialogOpen}
        onClose={() => { setDetailDialogOpen(false); setSelectedOrderId(null); }}
        orderId={selectedOrderId}
        onStatusUpdated={() => refetch()}
      />
    </Box>
  );
};

export default withTranslation()(MainSupplierOrdersView);
